import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  FilterAltOffRounded,
  Inventory2Outlined,
  SearchRounded,
} from '@mui/icons-material';
import DataTable from '../components/DataTable';
import AppPagination from '../components/AppPagination';
import AsyncAutocomplete from '../components/AsyncAutocomplete';
import { fetchProductos, deleteProducto } from '../api/productos';
import { fetchCategoriasPadre, fetchSubcategorias } from '../api/categorias';
import { fetchMarcas } from '../api/marcas';
import { fetchLowStock } from '../api/reports';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useToast } from '../contexts/ToastContext';

const formatMoney = (value) => `$ ${Number(value || 0).toFixed(2)}`;

const stockMeta = (stock) => {
  const available = Number(stock || 0);
  if (available <= 0) return { color: 'error', label: 'Sin stock' };
  if (available <= 2) return { color: 'warning', label: `${available} · Bajo` };
  return { color: 'success', label: `${available} disponible${available === 1 ? '' : 's'}` };
};

export default function Productos() {
  const nav = useNavigate();
  const location = useLocation();
  const restored = location.state?.restoreListState;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(restored?.search ?? '');
  const [categoria, setCategoria] = useState(restored?.categoria ?? '');
  const [subcategoria, setSubcategoria] = useState(restored?.subcategoria ?? '');
  const [marca, setMarca] = useState(restored?.marca ?? null);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [page, setPage] = useState(restored?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(restored?.rowsPerPage ?? 10);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState('todos');

  const { showToast } = useToast();
  const confirm = useConfirm();
  const { stop } = useLoading();

  const loadSubcategorias = async (id) => {
    if (!id) {
      setSubcategorias([]);
      return;
    }
    const response = await fetchSubcategorias(id);
    setSubcategorias(response.data);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      if (view === 'bajo') {
        const result = await fetchLowStock({ threshold: 2, page: page + 1, pageSize: rowsPerPage });
        setData(Array.isArray(result?.rows) ? result.rows : []);
        setTotal(Number(result?.total || 0));
        return;
      }
      const filters = { search, categoriaId: categoria, subcategoriaId: subcategoria, marcaId: marca?.id || '' };
      const paging = { page: page + 1, pageSize: rowsPerPage };
      const payload = await fetchProductos(filters, paging);

      if (Array.isArray(payload)) {
        setData(payload);
        setTotal(payload.length);
      } else {
        setData(Array.isArray(payload?.data) ? payload.data : []);
        setTotal(Number(payload?.total ?? 0));
      }
    } catch {
      showToast({ message: 'Error cargando productos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchCategoriasPadre().then((response) => setCategorias(response.data));
      if (restored?.categoria) await loadSubcategorias(restored.categoria);
      await refresh();
      stop();

      if (restored) nav(location.pathname, { replace: true, state: {} });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoria, subcategoria, marca, page, rowsPerPage, view]);

  const fetchMarcasFiltro = async ({ q, page, limit }) => {
    try {
      const response = await fetchMarcas({ nombre: q, page, pageSize: limit });
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      const pagination = response.data?.pagination;
      return {
        items: [
          ...(page === 1 ? [{ id: '', label: 'Todas las marcas' }] : []),
          ...rows.map((item) => ({ id: item.id, label: item.nombre })),
        ],
        hasMore: pagination ? Number(pagination.page) * Number(pagination.pageSize) < Number(pagination.total) : false,
      };
    } catch {
      showToast({ message: 'Error cargando marcas', severity: 'error' });
      return { items: [], hasMore: false };
    }
  };

  const handleDelete = async (row) => {
    const ok = await confirm({
      title: 'Eliminar producto',
      content: `¿Eliminar el producto "${row.nombre}"?`,
      confirmColor: 'error',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
    });
    if (!ok) return;

    try {
      await deleteProducto(row.id);
      showToast({ message: 'Producto eliminado', severity: 'success' });
      await refresh();
    } catch {
      showToast({ message: 'Error al eliminar', severity: 'error' });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Imagen',
        width: 72,
        sticky: 'left',
        accessor: (row) => {
          const imageUrl = row.imagen_url || '';
          const fallback = '/placeholder.png';
          if (!imageUrl) {
            return (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'action.hover',
                  color: 'text.secondary',
                }}
              >
                <Inventory2Outlined fontSize="small" />
              </Box>
            );
          }

          return (
            <Tooltip
              placement="right"
              arrow
              title={
                <Box
                  component="img"
                  src={imageUrl}
                  alt={row.nombre}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = fallback;
                  }}
                  sx={{ display: 'block', width: 190, height: 190, objectFit: 'cover', borderRadius: 1.5 }}
                />
              }
            >
              <Box
                component="img"
                src={imageUrl}
                alt={row.nombre}
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = fallback;
                }}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 2,
                  display: 'block',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              />
            </Tooltip>
          );
        },
      },
      { Header: 'SKU', accessor: (row) => row.sku || '—', width: 140 },
      { Header: 'Etiqueta', width: 120, accessor: (row) => (
        <Button size="small" component={RouterLink} to={`/etiquetas-barras?producto=${row.id}`}>
          Código de barras
        </Button>
      ) },
      {
        Header: 'Producto',
        width: 270,
        sticky: 'left',
        stickyOffset: 72,
        accessor: (row) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {row.nombre || 'Sin nombre'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[row.marca, row.presentacion].filter(Boolean).join(' · ') || 'Sin presentación'}
            </Typography>
          </Box>
        ),
      },
      {
        Header: 'Precio venta',
        accessor: (row) => (
          <Typography component="span" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(row.precio_venta)}
          </Typography>
        ),
        align: 'right',
        width: 140,
      },
      {
        Header: 'Stock',
        accessor: (row) => {
          const meta = stockMeta(row.stock);
          return <Chip size="small" color={meta.color} variant="outlined" label={meta.label} />;
        },
        align: 'right',
        width: 144,
      },
      {
        Header: 'Categoría',
        accessor: (row) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {[row.categoria, row.subcategoria].filter(Boolean).join(' / ') || '—'}
          </Typography>
        ),
        width: 210,
      },
    ],
    []
  );

  const hasFilters = Boolean(search || categoria || subcategoria || marca?.id);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2.5 }, pb: { xs: 3, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Inventory2Outlined color="primary" />
              <Typography variant="h5">Productos</Typography>
              <Chip size="small" color="primary" variant="outlined" label={`${total} registrados`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Consulta existencias, precios y catálogo desde una sola vista.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button variant="outlined" component={RouterLink} to="/etiquetas-barras">Etiquetas de barras</Button>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => nav('/productos/nuevo')}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Nuevo producto
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={view}
          onChange={(_, nextView) => { setView(nextView); setPage(0); }}
          variant="scrollable"
        >
          <Tab value="todos" label="Todos los productos" />
          <Tab value="bajo" label="Stock bajo (2 o menos)" />
        </Tabs>
      </Paper>

      {view === 'todos' && <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }}>
          <TextField
            label="Buscar producto"
            placeholder="Nombre o SKU"
            fullWidth
            size="small"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" fullWidth sx={{ minWidth: { lg: 190 } }}>
            <InputLabel>Categoría</InputLabel>
            <Select
              value={categoria}
              label="Categoría"
              onChange={(event) => {
                const value = event.target.value;
                setCategoria(value);
                setSubcategoria('');
                loadSubcategorias(value);
                setPage(0);
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categorias.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth disabled={!categoria} sx={{ minWidth: { lg: 190 } }}>
            <InputLabel>Subcategoría</InputLabel>
            <Select
              value={subcategoria}
              label="Subcategoría"
              onChange={(event) => {
                setSubcategoria(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Todas</MenuItem>
              {subcategorias.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ width: '100%', minWidth: { lg: 190 } }}>
            <AsyncAutocomplete
              label="Marca"
              placeholder="Todas las marcas"
              value={marca}
              fetchPage={fetchMarcasFiltro}
              popupMinWidth={240}
              onChange={(item) => {
                setMarca(item?.id ? item : null);
                setPage(0);
              }}
            />
          </Box>

          {hasFilters && (
            <Button
              color="inherit"
              startIcon={<FilterAltOffRounded />}
              onClick={() => {
                setSearch('');
                setCategoria('');
                setSubcategoria('');
                setMarca(null);
                setSubcategorias([]);
                setPage(0);
              }}
              sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', lg: 'center' } }}
            >
              Limpiar filtros
            </Button>
          )}
        </Stack>
      </Paper>}

      <DataTable
        rows={data}
        columns={columns}
        loading={loading}
        onEdit={(row) =>
          nav(`/productos/editar/${row.id}`, {
            state: { listState: { page, rowsPerPage, search, categoria, subcategoria, marca } },
          })
        }
        onDelete={handleDelete}
        stickyActionsRight
        maxHeight="min(56vh, 620px)"
      />

      <Paper sx={{ mt: 1.5, overflow: 'hidden' }}>
        <AppPagination
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10) || 10);
            setPage(0);
          }}
        />
      </Paper>
    </Container>
  );
}
